<div class="new-orders" *ngIf="currentFrac$ | async">
  <h2>New Order</h2>

  <h4 class="mb-0">Required</h4>
  <mat-divider></mat-divider>

  <div mat-dialog-content>
    <form [formGroup]="newOrderForm" class="new-orders-form" (ngSubmit)="placeOrder()">
      <mat-form-field>
        <mat-label>Select Mesh*</mat-label>
        <mat-select formControlName="meshId" saAutomatedSelector="lmo_orders_new__mesh_selector">
          <mat-optgroup *ngFor="let group of meshesInGroup$ | async" [label]="group.meshName">
            <mat-option *ngFor="let mesh of group.meshes" [value]="mesh.id">
              {{ mesh.type }}
              <span *ngIf="!mesh.isSupplemental; else isSupplementalMesh">
                Mesh
              </span>
            </mat-option>
          </mat-optgroup>
        </mat-select>
      </mat-form-field>

      <mat-form-field>
        <mat-label>Select Loader*</mat-label>
        <mat-select formControlName="loader" saAutomatedSelector="lmo_orders_new__loader_selector">
          <mat-select-trigger>
            <ng-container *ngIf="newOrderForm.get('loader').value as value; else selectLoader">
              <span [class.maxed-out]="isMaxedOut(value.fcpo?.purchaseOrder)">
                {{ value.site.name }}
              </span>
            </ng-container>
            <ng-template #selectLoader>
              Select Loader*
            </ng-template>
          </mat-select-trigger>
          <mat-option
            *ngFor="let mine of fracMines$ | async; trackBy: trackByFCPOId"
            [value]="mine"
            [class.mine-option]="!isMaxedOut(mine?.fcpo?.purchaseOrder)"
            [class.mine-option-maxed-out]="isMaxedOut(mine?.fcpo?.purchaseOrder)"
          >
            <div class="frac-mine-with-po" [class.maxed-out]="isMaxedOut(mine?.fcpo?.purchaseOrder)">
              <ng-container *ngIf="mine.downReport; else loaderUp">
                <span
                  class="mine-name warn"
                  [mdePopoverTriggerFor]="siteDownWarning"
                  mdePopoverTriggerOn="hover"
                  [mdePopoverOverlapTrigger]="false"
                  >{{ mine?.site?.name }}
                  <mat-icon class="warning">warning</mat-icon>
                </span>
                <mde-popover #siteDownWarning="mdePopover">
                  <div class="down-report-message">
                    <div class="header">
                      Loader Reported Down at {{ mine.downReport.createdTimestamp | date: 'short' }}
                    </div>
                    <div
                      class="sub-header"
                      *ngIf="mine.downReport.description && mine.downReport.description.length > 0"
                    >
                      {{ mine.downReport.reportedUser }} reported {{ mine.downReport.site?.name }} down for
                      {{ mine.downReport.eta | minutesToHuman }}: "{{ mine.downReport.description }}".
                    </div>

                    <div
                      class="sub-header"
                      *ngIf="!mine.downReport.description || mine.downReport.description.length === 0"
                    >
                      {{ mine.downReport.reportedUser }} reported {{ mine.downReport.site?.name }} down for
                      {{ mine.downReport.eta | minutesToHuman }}
                    </div>
                  </div>
                </mde-popover>
              </ng-container>
              <ng-template #loaderUp>
                <div class="mine-name">{{ mine?.site?.name }}</div>
              </ng-template>
              <div class="po-name">PO: {{ mine?.fcpo?.purchaseOrder?.name }}</div>
              <div class="wait-time" *ngIf="mine?.waitTime > 0">
                {{ mine?.waitTime | minutesToHuman }} est. In & Out Time
              </div>
              <div class="wait-time" *ngIf="mine?.waitTime === 0">-- est. In & Out Time</div>
              <div class="po-remaining">
                {{ mine?.fcpo?.purchaseOrder?.meshType }} mesh remaining:
                {{ calculatePoRemaining(mine?.fcpo?.purchaseOrder) | approxBigNumber }} lbs.
              </div>
            </div>
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field>
        <input
          matInput
          placeholder="Quantity of Trucks*"
          value=""
          type="number"
          autocomplete="off"
          formControlName="numberOfTrucks"
          saAutomatedSelector="lmo_orders_new__count"
        />
      </mat-form-field>

      <mat-form-field>
        <mat-label>Select Trucking Vendor*</mat-label>
        <mat-select formControlName="truckingVendorId" saAutomatedSelector="lmo_orders_new__trucking_vendor">
          <mat-option
            *ngFor="let fctv of truckingVendors$ | async; trackBy: trackByVendorId"
            [value]="fctv.truckingVendorId"
          >
            {{ fctv.truckingVendorName }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <h4 class="mb-0">Optional</h4>
      <mat-divider></mat-divider>
      <mat-form-field class="form-field">
        <input
          matInput
          class="datepicker-input"
          placeholder="Pickup Time"
          autocomplete="off"
          formControlName="pickupTime"
          [owlDateTime]="pickupTime"
          [owlDateTimeTrigger]="pickupTime"
        />
        <span [owlDateTimeTrigger]="pickupTime"><i class="material-icons">calendar_today</i></span>
        <owl-date-time #pickupTime></owl-date-time>
      </mat-form-field>

      <mat-form-field>
        <input
          matInput
          placeholder="Stagger (Minutes)"
          value=""
          type="number"
          autocomplete="off"
          formControlName="staggerMinutes"
        />
      </mat-form-field>

      <mat-form-field *saFeatureFlag="'orderTrailerType'">
        <mat-label>Trailer Type</mat-label>
        <mat-select formControlName="trailerTypeId" saAutomatedSelector="lmo_orders_new__trailer_type_selector">
          <mat-option
            *ngFor="let trailerType of trailerTypes$ | async; trackBy: trackByTrailerType"
            [value]="trailerType.id"
          >
            {{ trailerType.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <div *saFeatureFlag="'showOrderCreateBoxPickup'" class="border border-gray-400 p-2 flex flex-col">
        <div class="flex flex-row">
          <mat-slide-toggle formControlName="createBoxPickup">
            Create Box Pickup
          </mat-slide-toggle>
          <mat-slide-toggle class="ml-1" formControlName="firstStop">
            1st Stop
          </mat-slide-toggle>
        </div>

        <mat-form-field>
          <mat-label>Pickup Site</mat-label>
          <mat-select formControlName="boxPickupSiteId">
            <mat-option>
              <ngx-mat-select-search
                placeholderLabel="Site"
                noEntriesFoundLabel="Site not found"
                [formControl]="searchForm"
              ></ngx-mat-select-search>
            </mat-option>
            <mat-option *ngFor="let site of boxPickupSites$ | async; trackBy: trackByBoxPickupSiteId" [value]="site">{{
              site.name
            }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field>
          <mat-label>OOR Miles</mat-label>
          <input matInput sa-number-input formControlName="outOfRouteMiles" />
        </mat-form-field>
        <mat-form-field>
          <mat-label>$/Mile</mat-label>
          <input matInput saCurrencyInput formControlName="outOfRouteRateCents" />
        </mat-form-field>
      </div>

      <button
        mat-raised-button
        color="primary"
        [disabled]="newOrderForm.invalid"
        saAutomatedSelector="lmo_orders_new__place_order"
      >
        Order
      </button>
    </form>
  </div>
</div>

<ng-template #exceedsAllocations>
  <h3>Sand Ordered Exceeds Allocation</h3>
  <ng-container *ngIf="(getDailyAllocationOverageAmount$() | async) > 0">
    <p>
      These orders will exceed today's daily allocation by
      {{ getDailyAllocationOverageAmount$() | async | approxBigNumber }} lbs
    </p>
  </ng-container>
  <ng-container *ngIf="(getTotalAllocationOverageAmount$() | async) > 0">
    <p>
      These orders will exceed your total allocation by
      {{ getTotalAllocationOverageAmount$() | async | approxBigNumber }} lbs
    </p>
  </ng-container>
  <div class="action-row">
    <button mat-stroked-button (click)="confirmOverAllocationOrder.close(false)">Go Back</button>
    <button mat-stroked-button (click)="confirmOverAllocationOrder.close(true)" color="warn">Order Anyway</button>
  </div>
</ng-template>

<ng-template #isSupplementalMesh>
  Mesh – Supplemental
</ng-template>
